import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  DollarSign,
  AlertCircle,
  Filter,
  Calendar,
  Building,
  ExternalLink,
  CheckCircle,
  Clock,
  XCircle,
  Plus,
  Link,
  Edit,
  FolderOpen,
  ChevronDown,
  ChevronUp,
  Receipt,
} from "lucide-react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Badge,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Dialog,
  DialogContent,
  Input,
  Label,
} from "@/components/ui";
import { toast } from "react-hot-toast";
import financeService from "@/services/financeService";
import PaymentRecordingModal from "./components/PaymentRecordingModal";
import CreatePayableModal from "./components/CreatePayableModal";
import LinkPurchaseRequestsModal from "./components/LinkPurchaseRequestsModal";

const SUBSIDIARIES = [
  { value: "", label: "All Subsidiaries" },
  { value: "puffin_group", label: "Puffin Group Ltd" },
  { value: "buildwise", label: "Buildwise Construction Ltd" },
  { value: "laser_shield", label: "Laser Shield Ltd." },
  { value: "maz_logistics", label: "Maz Logistics Ltd." },
  { value: "puffin_energy", label: "Puffin Energy Ltd." },
  { value: "swingtide", label: "Swingtide Consulting Ltd" },
  { value: "puffin_agro", label: "Puffin Agro Allied" },
];

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "Pending Payment", label: "Pending Payment" },
  { value: "Partially Paid", label: "Partially Paid" },
  { value: "Fully Paid", label: "Fully Paid" },
];

const AGING_OPTIONS = [
  { value: "", label: "All Ages" },
  { value: "30", label: "0-30 days" },
  { value: "60", label: "31-60 days" },
  { value: "90", label: "61-90 days" },
  { value: "91", label: "90+ days" },
];

const PayablesManagement = () => {
  const navigate = useNavigate();
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({
    total: 0,
    paid: 0,
    outstanding: 0,
  });
  const [filters, setFilters] = useState({
    status: "",
    subsidiary: "",
    aging: "",
  });
  const [selectedPayable, setSelectedPayable] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLinkPRModal, setShowLinkPRModal] = useState(false);
  const [showAdjustAmountModal, setShowAdjustAmountModal] = useState(false);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    fetchPayables();
  }, [filters]);

  const fetchPayables = async () => {
    try {
      setLoading(true);
      const response = await financeService.getPayables(filters);
      
      if (response.data.success) {
        const payablesData = response.data.data.payables || [];
        
        // Fetch payment history for each payable
        const payablesWithPayments = await Promise.all(
          payablesData.map(async (payable) => {
            try {
              const paymentsResponse = await financeService.getPayablePaymentHistory(payable.id);
              const payments = paymentsResponse.data.success ? paymentsResponse.data.data.payments || [] : [];
              return { ...payable, Payments: payments };
            } catch (error) {
              console.warn(`Failed to fetch payments for payable ${payable.id}:`, error);
              return { ...payable, Payments: [] };
            }
          })
        );
        
        setPayables(payablesWithPayments);
        
        // Calculate summary
        const total = payablesWithPayments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
        const paid = payablesWithPayments.reduce((sum, p) => sum + parseFloat(p.paid_amount || 0), 0);
        const outstanding = total - paid;
        
        setSummary({ total, paid, outstanding });
      }
    } catch (error) {
      console.error("Error fetching payables:", error);
      toast.error("Failed to load payables");
    } finally {
      setLoading(false);
    }
  };

  const getAgingColor = (days) => {
    if (days <= 30) return "text-green-600 bg-green-50";
    if (days <= 60) return "text-yellow-600 bg-yellow-50";
    if (days <= 90) return "text-orange-600 bg-orange-50";
    return "text-red-600 bg-red-50";
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      "Pending Payment": { color: "bg-orange-100 text-orange-800", icon: Clock },
      "Partially Paid": { color: "bg-blue-100 text-blue-800", icon: AlertCircle },
      "Fully Paid": { color: "bg-green-100 text-green-800", icon: CheckCircle },
    };
    
    const config = statusConfig[status] || { color: "bg-gray-100 text-gray-800", icon: XCircle };
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  const handleRecordPayment = (payable) => {
    setSelectedPayable(payable);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setSelectedPayable(null);
    fetchPayables();
    toast.success("Payment recorded successfully");
  };

  const handleCreateSuccess = () => {
    setShowCreateModal(false);
    fetchPayables();
    toast.success("Payable created successfully");
  };

  const handleLinkPRSuccess = () => {
    fetchPayables();
  };

  const handleLinkPR = (payable) => {
    setSelectedPayable(payable);
    setShowLinkPRModal(true);
  };

  const handleAdjustAmount = (payable) => {
    setSelectedPayable(payable);
    setAdjustAmount(payable.total_amount || payable.amount);
    setShowAdjustAmountModal(true);
  };

  const handleAdjustAmountSubmit = async () => {
    if (!adjustAmount || parseFloat(adjustAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    const paidAmount = parseFloat(selectedPayable.paid_amount || 0);
    if (parseFloat(adjustAmount) < paidAmount) {
      toast.error(`Amount cannot be less than already paid amount (${formatCurrency(paidAmount)})`);
      return;
    }

    try {
      await financeService.updatePayableAmount(selectedPayable.id, parseFloat(adjustAmount));
      toast.success("Payable amount updated successfully");
      setShowAdjustAmountModal(false);
      setSelectedPayable(null);
      setAdjustAmount("");
      fetchPayables();
    } catch (error) {
      console.error("Error updating payable amount:", error);
      toast.error(error.response?.data?.message || "Failed to update amount");
    }
  };

  const toggleRowExpansion = (payableId) => {
    setExpandedRow(expandedRow === payableId ? null : payableId);
  };

  const formatCurrency = (amount) => {
    return financeService.formatCurrency(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading payables...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Accounts Payable
          </h1>
          <p className="text-gray-500 mt-1 flex items-center gap-2">
            <span>Manage vendor payments and obligations</span>
            <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-medium">
              {payables.length} Active
            </span>
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 shadow-lg hover:shadow-xl transition-all duration-200"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Payable
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-red-400 to-red-600"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Payables</p>
                </div>
                <p className="text-3xl font-bold text-gray-900 mb-1">
                  {formatCurrency(summary.total)}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  All outstanding obligations
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <FileText className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-green-400 to-green-600"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Paid</p>
                </div>
                <p className="text-3xl font-bold text-green-600 mb-1">
                  {formatCurrency(summary.paid)}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  Payments completed
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <CheckCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 group">
          <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-orange-400 to-orange-600"></div>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Outstanding</p>
                </div>
                <p className="text-3xl font-bold text-orange-600 mb-1">
                  {formatCurrency(summary.outstanding)}
                </p>
                <p className="text-xs text-gray-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Amount pending
                </p>
              </div>
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                <AlertCircle className="h-8 w-8 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-md">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
          <CardTitle className="flex items-center gap-2 text-lg">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <Filter className="h-4 w-4 text-red-600" />
            </div>
            <span>Filters</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 block">
                Status
              </label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger className="border-gray-200 focus:border-red-500 focus:ring-red-500">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 block">
                Subsidiary
              </label>
              <Select
                value={filters.subsidiary}
                onValueChange={(value) => setFilters({ ...filters, subsidiary: value })}
              >
                <SelectTrigger className="border-gray-200 focus:border-red-500 focus:ring-red-500">
                  <SelectValue placeholder="All Subsidiaries" />
                </SelectTrigger>
                <SelectContent>
                  {SUBSIDIARIES.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2 block">
                Aging
              </label>
              <Select
                value={filters.aging}
                onValueChange={(value) => setFilters({ ...filters, aging: value })}
              >
                <SelectTrigger className="border-gray-200 focus:border-red-500 focus:ring-red-500">
                  <SelectValue placeholder="All Ages" />
                </SelectTrigger>
                <SelectContent>
                  {AGING_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payables List */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-red-50 via-white to-red-50 border-b border-red-100">
          <CardTitle className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-red-600 to-red-700 rounded-xl flex items-center justify-center shadow-md">
              <FileText className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1">
              <span className="text-xl font-bold text-gray-900">Payables List</span>
              <p className="text-xs text-gray-500 mt-0.5">Click to expand and view payment details</p>
            </div>
            <Badge className="bg-red-100 text-red-700 border-red-200 px-3 py-1">
              {payables.length} items
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {payables.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No payables found</p>
              <p className="text-sm text-gray-400 mt-1">
                Payables will appear here when purchase requests are approved
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {payables.map((payable) => {
                const paidFromPayments = payable.Payments && Array.isArray(payable.Payments)
                  ? payable.Payments.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0)
                  : parseFloat(payable.paid_amount || 0);
                
                const balance = parseFloat(payable.amount || 0) - paidFromPayments;
                const aging = payable.aging_days || 0;
                const isExpanded = expandedRow === payable.id;
                
                return (
                  <div key={payable.id} className={`transition-all duration-200 ${isExpanded ? 'bg-red-50/30' : 'hover:bg-gray-50'}`}>
                    {/* Main Row */}
                    <div className="p-5">
                      <div className="flex items-center justify-between gap-4">
                        {/* Left Section - Vendor & Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => toggleRowExpansion(payable.id)}
                              className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 ${
                                isExpanded 
                                  ? 'bg-red-600 text-white shadow-md' 
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </button>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 mb-1.5">
                                <h3 className="font-bold text-gray-900 truncate text-lg">
                                  {payable.beneficiary_name || payable.vendor_name}
                                </h3>
                                {getStatusBadge(payable.payment_status || payable.status)}
                              </div>
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                {payable.PurchaseRequest && (
                                  <>
                                    <button
                                      onClick={() => navigate(`/purchase-requests/${payable.PurchaseRequest.id}`)}
                                      className="font-mono bg-gray-100 px-2 py-0.5 rounded text-xs font-semibold hover:bg-gray-200 transition-colors flex items-center gap-1"
                                    >
                                      <ExternalLink className="h-3 w-3" />
                                      PR: {payable.PurchaseRequest.request_id}
                                    </button>
                                  </>
                                )}
                                {payable.Project && (
                                  <>
                                    {payable.PurchaseRequest && <span className="text-gray-300">•</span>}
                                    <div className="flex items-center gap-1.5">
                                      <FolderOpen className="h-3.5 w-3.5 text-red-500" />
                                      <span className="truncate font-medium">{payable.Project.name}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Middle Section - Amounts */}
                        <div className="flex items-center gap-8">
                          <div className="text-right">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Amount</p>
                            <div className="flex items-center gap-2">
                              <p className="font-bold text-gray-900 text-base">{formatCurrency(payable.total_amount || payable.amount)}</p>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleAdjustAmount(payable)}
                                className="h-6 w-6 p-0 hover:bg-gray-100"
                                title="Adjust amount"
                              >
                                <Edit className="h-3 w-3 text-gray-500" />
                              </Button>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Paid</p>
                            <p className="font-bold text-green-600 text-base">{formatCurrency(paidFromPayments)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Balance</p>
                            <p className="font-bold text-red-600 text-base">{formatCurrency(balance)}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Aging</p>
                            <Badge className={`${getAgingColor(aging)} font-bold`}>
                              {aging}d
                            </Badge>
                          </div>
                        </div>

                        {/* Right Section - Actions */}
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleLinkPR(payable)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Link className="h-4 w-4 mr-1" />
                            Link PR
                          </Button>
                          {payable.payment_status !== "Fully Paid" && payable.status !== "Fully Paid" && (
                            <Button
                              size="sm"
                              onClick={() => handleRecordPayment(payable)}
                              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-md hover:shadow-lg transition-all duration-200 font-semibold"
                            >
                              <Plus className="h-4 w-4 mr-1.5" />
                              Add Payment
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-5 pb-5 bg-gradient-to-b from-red-50/50 to-white border-t border-red-100">
                        <div className="grid grid-cols-12 gap-6 pt-5">
                          {/* Left Column - Info */}
                          <div className="col-span-4 space-y-4">
                            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                                <Building className="h-3.5 w-3.5" />
                                Subsidiary
                              </label>
                              <p className="text-sm font-semibold text-gray-900">
                                {SUBSIDIARIES.find(s => s.value === payable.subsidiary)?.label || payable.subsidiary}
                              </p>
                            </div>
                            
                            {payable.description && (
                              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">
                                  Description
                                </label>
                                <p className="text-sm text-gray-700 leading-relaxed">
                                  {payable.description}
                                </p>
                              </div>
                            )}

                            <div className="grid grid-cols-1 gap-3">
                              <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 shadow-md text-white">
                                <p className="text-xs font-bold uppercase tracking-wider opacity-90 mb-1">Total Amount</p>
                                <p className="text-2xl font-bold">
                                  {formatCurrency(payable.total_amount || payable.amount)}
                                </p>
                              </div>
                              <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 shadow-md text-white">
                                <p className="text-xs font-bold uppercase tracking-wider opacity-90 mb-1">Total Paid</p>
                                <p className="text-2xl font-bold">
                                  {formatCurrency(paidFromPayments)}
                                </p>
                              </div>
                              <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 shadow-md text-white">
                                <p className="text-xs font-bold uppercase tracking-wider opacity-90 mb-1">Balance Due</p>
                                <p className="text-2xl font-bold">
                                  {formatCurrency(balance)}
                                </p>
                              </div>
                            </div>
                          </div>

                          {/* Right Column - Payment History */}
                          <div className="col-span-8">
                            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                              <div className="bg-gradient-to-r from-gray-50 to-white px-4 py-3 border-b border-gray-200">
                                <div className="flex items-center gap-2">
                                  <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
                                    <Receipt className="h-4 w-4 text-red-600" />
                                  </div>
                                  <h4 className="font-bold text-gray-900">Payment History</h4>
                                  <Badge className="bg-red-100 text-red-700 border-red-200">
                                    {payable.Payments?.length || 0} payments
                                  </Badge>
                                </div>
                              </div>

                              <div className="p-4">
                                {!payable.Payments || payable.Payments.length === 0 ? (
                                  <div className="bg-gray-50 rounded-lg p-8 text-center border-2 border-dashed border-gray-200">
                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                      <Receipt className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <p className="text-sm font-semibold text-gray-600">No payments recorded yet</p>
                                    <p className="text-xs text-gray-400 mt-1">Payments will appear here once recorded</p>
                                  </div>
                                ) : (
                                  <div className="space-y-3 max-h-72 overflow-y-auto pr-2">
                                    {payable.Payments.map((payment, index) => (
                                      <div
                                        key={payment.id || index}
                                        className="bg-gradient-to-r from-green-50 to-white border border-green-100 rounded-lg p-4 hover:shadow-md transition-all duration-200"
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                                              <CheckCircle className="h-5 w-5 text-white" />
                                            </div>
                                            <div>
                                              <p className="font-bold text-gray-900 text-lg">
                                                {formatCurrency(payment.amount)}
                                              </p>
                                              <div className="flex items-center gap-2 mt-1">
                                                <Calendar className="h-3 w-3 text-gray-400" />
                                                <p className="text-xs text-gray-600 font-medium">
                                                  {financeService.formatDate(payment.payment_date || payment.createdAt)}
                                                </p>
                                              </div>
                                            </div>
                                          </div>
                                          <div className="text-right">
                                            {payment.payment_method && (
                                              <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-1.5 text-xs font-semibold">
                                                {payment.payment_method}
                                              </Badge>
                                            )}
                                            {payment.reference_number && (
                                              <p className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                                                {payment.reference_number}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        {payment.notes && (
                                          <div className="mt-3 pt-3 border-t border-green-100">
                                            <p className="text-xs text-gray-600 italic">{payment.notes}</p>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Payable Modal */}
      <CreatePayableModal
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={handleCreateSuccess}
      />

      {/* Link Purchase Requests Modal */}
      {showLinkPRModal && selectedPayable && (
        <LinkPurchaseRequestsModal
          open={showLinkPRModal}
          onOpenChange={setShowLinkPRModal}
          payable={selectedPayable}
          onSuccess={handleLinkPRSuccess}
        />
      )}

      {/* Adjust Amount Modal */}
      <Dialog open={showAdjustAmountModal} onOpenChange={setShowAdjustAmountModal}>
        <DialogContent className="max-w-md">
          <div className="space-y-4">
            <div className="border-b pb-4">
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Edit className="h-5 w-5 text-blue-600" />
                Adjust Payable Amount
              </h2>
              <p className="text-gray-600 mt-1">
                Update the total amount for this payable
              </p>
            </div>

            {selectedPayable && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                  <p className="text-sm text-gray-600">Vendor</p>
                  <p className="font-medium">{selectedPayable.beneficiary_name || selectedPayable.vendor_name}</p>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg space-y-1">
                  <p className="text-sm text-gray-600">Current Amount</p>
                  <p className="font-semibold text-lg">{formatCurrency(selectedPayable.total_amount || selectedPayable.amount)}</p>
                </div>

                {parseFloat(selectedPayable.paid_amount || 0) > 0 && (
                  <div className="bg-amber-50 p-3 rounded-lg space-y-1">
                    <p className="text-sm text-amber-700">Already Paid</p>
                    <p className="font-semibold text-amber-900">{formatCurrency(selectedPayable.paid_amount)}</p>
                    <p className="text-xs text-amber-600 mt-1">
                      New amount must be greater than or equal to paid amount
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="adjust_amount" className="text-sm font-medium text-gray-700">
                    New Amount <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative mt-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-4 w-4 text-gray-400" />
                    </div>
                    <Input
                      id="adjust_amount"
                      type="number"
                      step="0.01"
                      min={selectedPayable.paid_amount || 0.01}
                      value={adjustAmount}
                      onChange={(e) => setAdjustAmount(e.target.value)}
                      className="pl-10"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAdjustAmountModal(false);
                      setSelectedPayable(null);
                      setAdjustAmount("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAdjustAmountSubmit}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Update Amount
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Recording Modal */}
      {showPaymentModal && selectedPayable && (
        <PaymentRecordingModal
          open={showPaymentModal}
          onOpenChange={setShowPaymentModal}
          payable={selectedPayable}
          onSuccess={handlePaymentSuccess}
          type="payable"
        />
      )}
    </div>
  );
};

export default PayablesManagement;
